import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Stack,
  LinearProgress,
  IconButton,
  Grid,
  Tabs,
  Tab,
  Tooltip,
  CircularProgress,
  Chip,
  Slider,
} from "@mui/material";
import {
  Person,
  AutoFixHigh,
  ContentCopy,
  VerifiedUser,
  Psychology,
  School,
  Bolt,
} from "@mui/icons-material";
import axios from "axios";
import API_BASE_URL from "../config";
import { auth } from "../firebase";

interface AIHumanizerProps {
  darkMode: boolean;
  setDarkMode: (value: boolean) => void;
}

export default function AIHumanizer({
  darkMode,
}: AIHumanizerProps): JSX.Element {
  const [inputText, setInputText] = useState("");
  const [outputText, setOutputText] = useState("");
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState("standard");
  const [copied, setCopied] = useState(false);
  const [preferences, setPreferences] = useState<string>(
    () => localStorage.getItem("humanizer_preferences") || ""
  );
  const [feedbackStatus, setFeedbackStatus] = useState<
    "none" | "loading" | "success"
  >("none");
  const [pendingLabel, setPendingLabel] = useState<"Human" | "AI" | null>(null);
  const [naturalnessScore, setNaturalnessScore] = useState<number>(80);
  const [affectedSections, setAffectedSections] = useState<string[]>([]);
  const [feedbackNotes, setFeedbackNotes] = useState("");
  const [feedbackTags, setFeedbackTags] = useState<string[]>([]);
  const [improving, setImproving] = useState(false);
  const [judgeScore, setJudgeScore] = useState<{ human_score: number; fluency: number; issues: string[] } | null>(null);
  const [requestId, setRequestId] = useState<string | null>(null);
  const [jobStatus, setJobStatus] = useState<"pending" | "processing" | "done" | "error" | null>(null);
  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [externalTool, setExternalTool] = useState<string>("Quillbot");
  const [externalScore, setExternalScore] = useState<number | "">("");
  const [externalReportStatus, setExternalReportStatus] = useState<"none" | "loading" | "success" | "error">("none");

  const getUid = (): string | null => auth?.currentUser?.uid ?? null;

  const handleModeChange = (event: React.SyntheticEvent, newValue: string) => {
    setMode(newValue);
  };

  // Cleanup polling on unmount
  useEffect(() => () => { if (pollRef.current) clearTimeout(pollRef.current); }, []);

  const wordCount = inputText.split(/\s+/).filter((x) => x).length;
  const isLongRequest = mode === "academic" || wordCount > 500;

  const resetOutputState = () => {
    setOutputText("");
    setFeedbackStatus("none");
    setPendingLabel(null);
    setNaturalnessScore(80);
    setAffectedSections([]);
    setFeedbackNotes("");
    setFeedbackTags([]);
    setJudgeScore(null);
    setJobStatus(null);
    setRequestId(null);
  };

  const trackAction = (action: "copy" | "edit" | "discard" | "improve") => {
    if (!requestId) return;
    axios.post(`${API_BASE_URL}/track-action`, { request_id: requestId, action }).catch(() => {});
  };

  const startPolling = (jobId: string) => {
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API_BASE_URL}/job/${jobId}`);
        if (data.status === "done") {
          setOutputText(data.result ?? "");
          if (data.judge) setJudgeScore(data.judge);
          setRequestId(jobId);
          setJobStatus("done");
          setLoading(false);
        } else if (data.status === "error") {
          setOutputText("[Error: The AI service could not process your request. Please try again.]");
          setJobStatus("error");
          setLoading(false);
        } else {
          setJobStatus(data.status as "pending" | "processing");
          pollRef.current = setTimeout(poll, 3000);
        }
      } catch {
        setOutputText("[Error: Could not check job status. Please try again.]");
        setJobStatus("error");
        setLoading(false);
      }
    };
    pollRef.current = setTimeout(poll, 3000);
  };

  const handleHumanize = async () => {
    if (!inputText.trim()) return;
    if (pollRef.current) clearTimeout(pollRef.current);
    setLoading(true);
    resetOutputState();

    try {
      if (isLongRequest) {
        // Async flow — Faz 2.2
        const { data } = await axios.post(`${API_BASE_URL}/humanize-async`, {
          text: inputText,
          mode,
          preferences,
          uid: getUid(),
        }, { timeout: 75000 });
        setJobStatus("pending");
        startPolling(data.job_id);
      } else {
        // Sync flow (standard / creative / formal, ≤500 words)
        const response = await axios.post(`${API_BASE_URL}/humanize-text`, {
          text: inputText,
          mode,
          preferences,
          uid: getUid(),
        }, { timeout: 90000 });

        if (response.data?.result) {
          setOutputText(response.data.result);
          if (response.data.judge) setJudgeScore(response.data.judge);
          if (response.data.request_id) setRequestId(response.data.request_id);
        }
        setLoading(false);
      }
    } catch (error: any) {
      console.error("Humanizing failed:", error);
      const isTimeout = error.code === "ECONNABORTED" || String(error.message).includes("timeout");
      setOutputText(
        isTimeout
          ? "[Server is starting up — please wait a moment and try again.]"
          : "[Error: The AI service could not process your request. Please try again.]"
      );
      setLoading(false);
    }
  };

  const handleRetryHumanize = async () => {
    // Same as Humanize, but keeps current input and preferences
    await handleHumanize();
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData("text");
    if (!pastedText) return;

    // Smart Clean Logic for PDF/Academic copies:
    const cleaned = pastedText
      .replace(/(\S)-\r?\n(\S)/g, "$1$2") // Join hyphenated words (e.g., 'architec- \n ture')
      .replace(/([^\n])\n([^\n])/g, "$1 $2") // Join single line breaks into a continuous sentence
      .replace(/\n\s*\n/g, "\n\n") // Preserve and normalize paragraph breaks
      .replace(/[ \t]+/g, " ") // Collapse extra spaces
      .trim();

    // Insert at selection position
    const target = e.target as HTMLTextAreaElement;
    const start = target.selectionStart;
    const end = target.selectionEnd;
    const newText =
      inputText.substring(0, start) + cleaned + inputText.substring(end);

    setInputText(newText);
  };

  const handleClear = () => {
    if (pollRef.current) clearTimeout(pollRef.current);
    setInputText("");
    resetOutputState();
    setLoading(false);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(outputText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    trackAction("copy");
  };

  const getModeIcon = (m: string) => {
    switch (m) {
      case "standard":
        return <Person fontSize="small" />;
      case "academic":
        return <School fontSize="small" />;
      case "creative":
        return <Psychology fontSize="small" />;
      case "aggressive":
        return <Bolt fontSize="small" />;
      default:
        return <Person fontSize="small" />;
    }
  };

  const toggleTag = (tag: string) => {
    setFeedbackTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  };

  const savePreferences = (value: string) => {
    setPreferences(value);
    localStorage.setItem("humanizer_preferences", value);
  };

  const updateLocalProfileAndPreferences = (tags: string[], currentPrefs: string) => {
    const profileRaw = localStorage.getItem("humanizer_profile_v1");
    const profile: Record<string, number> = profileRaw ? JSON.parse(profileRaw) : {};
    for (const t of tags) profile[t] = (profile[t] || 0) + 1;
    localStorage.setItem("humanizer_profile_v1", JSON.stringify(profile));

    const hints: string[] = [];
    const addHintIf = (tag: string, hint: string, minCount = 1) => {
      if ((profile[tag] || 0) >= minCount) hints.push(hint);
    };

    addHintIf("Too wordy", "Prefer concise sentences; remove filler.", 1);
    addHintIf("Too formal", "Use a natural tone; avoid overly stiff phrasing.", 1);
    addHintIf("Too robotic", "Vary sentence openings and rhythm; avoid templated transitions.", 1);
    addHintIf("Bad flow", "Improve cohesion between sentences; ensure each paragraph has a clear through-line.", 1);
    addHintIf("Grammar issues", "Be careful with grammar and punctuation.", 1);
    addHintIf("Changed meaning", "Preserve meaning strictly; do not introduce new claims.", 1);
    addHintIf("AI-like punctuation", "Avoid excessive em-dashes (—) or double dashes (--). Use standard human punctuation.", 1);

    if (hints.length === 0) return;
    const uniqueHints = hints.filter((h, idx) => hints.indexOf(h) === idx);
    const autoBlock = `Auto profile: ${uniqueHints.join(" ")}`;

    const next =
      currentPrefs && currentPrefs.includes("Auto profile:")
        ? currentPrefs.replace(/Auto profile:[\s\S]*$/m, autoBlock)
        : (currentPrefs ? `${currentPrefs}\n\n${autoBlock}` : autoBlock);

    savePreferences(next);
  };

  const submitFeedback = async () => {
    if (!outputText || !pendingLabel) return;
    setFeedbackStatus("loading");
    try {
      await axios.post(`${API_BASE_URL}/humanize-feedback`, {
        input_text: inputText,
        output_text: outputText,
        mode,
        naturalness_score: naturalnessScore,
        affected_sections: affectedSections,
        tags: feedbackTags,
        notes: feedbackNotes,
        label: pendingLabel,
        uid: getUid(),
      });
      setFeedbackStatus("success");
      updateLocalProfileAndPreferences(feedbackTags, preferences);
    } catch (e) {
      console.error("Feedback send failed:", e);
      setFeedbackStatus("none");
    }
  };

  const reportExternalScore = async () => {
    if (!outputText.trim() || externalScore === "") return;
    setExternalReportStatus("loading");
    try {
      await axios.post(`${API_BASE_URL}/report-external-score`, {
        text: outputText,
        external_score: Number(externalScore),
        tool: externalTool,
        mode,
      });
      setExternalReportStatus("success");
      setExternalScore("");
      setTimeout(() => setExternalReportStatus("none"), 3000);
    } catch (e) {
      console.error("External report failed:", e);
      setExternalReportStatus("error");
      setTimeout(() => setExternalReportStatus("none"), 3000);
    }
  };

  const improveWithFeedback = async () => {
    if (!inputText.trim() || !outputText.trim()) return;
    setImproving(true);
    try {
      const resp = await axios.post(`${API_BASE_URL}/humanize-improve`, {
        input_text: inputText,
        output_text: outputText,
        mode,
        preferences,
        naturalness_score: naturalnessScore,
        affected_sections: affectedSections,
        tags: feedbackTags,
        notes: feedbackNotes,
        iterations: 2,
      }, { timeout: 90000 });
      if (resp.data?.result) {
        setOutputText(resp.data.result);
        setFeedbackStatus("none");
        setPendingLabel(null);
        trackAction("improve");
      }
    } catch (e) {
      console.error("Improve failed:", e);
    } finally {
      setImproving(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto", pt: 4 }}>
      <Box sx={{ mb: 6 }}>
        <Typography
          variant="h3"
          sx={{
            fontWeight: 600,
            mb: 1,
            trackingTight: "-0.02em",
            color: darkMode ? "white" : "text.primary",
          }}
        >
          Text <span style={{ color: "#3b82f6" }}>Humanizer</span>
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: darkMode ? "#9ca3af" : "text.secondary",
            fontWeight: 400,
            maxWidth: 600,
          }}
        >
          Transform AI-generated content into natural human-like writing. Select
          a mode to tailor the style.
        </Typography>
      </Box>

      {/* Mode Selection Tabs */}
      <Box sx={{ mb: 3 }}>
        <Tabs
          value={mode}
          onChange={handleModeChange}
          variant="scrollable"
          scrollButtons="auto"
          textColor="inherit"
          indicatorColor="primary"
          sx={{
            "& .MuiTab-root": {
              color: darkMode ? "#9ca3af" : "text.secondary",
              minHeight: 48,
              textTransform: "none",
              fontWeight: 600,
              fontSize: "15px",
            },
            "& .Mui-selected": { color: "#3b82f6" },
            "& .MuiTabs-indicator": { backgroundColor: "#3b82f6", height: 3 },
          }}
        >
          <Tab
            icon={<Person fontSize="small" />}
            iconPosition="start"
            label="Standard"
            value="standard"
          />
          <Tab
            icon={<School fontSize="small" />}
            iconPosition="start"
            label="Academic"
            value="academic"
          />
          <Tab
            icon={<Psychology fontSize="small" />}
            iconPosition="start"
            label="Creative"
            value="creative"
          />
          <Tab
            icon={<Bolt fontSize="small" />}
            iconPosition="start"
            label="Aggressive (Strong rewrite)"
            value="aggressive"
            sx={{ color: "#f59e0b !important" }} // Highlight aggressive mode
          />
        </Tabs>
      </Box>

      {/* Preferences */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: "18px",
          bgcolor: darkMode ? "#060606" : "background.paper",
          border: `1px solid ${darkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.06)"}`,
          p: 2.5,
          mb: 3,
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontWeight: 700,
            color: darkMode ? "#e5e7eb" : "text.primary",
            mb: 1,
          }}
        >
          Style preferences (optional)
        </Typography>
        <Typography
          variant="caption"
          sx={{
            color: darkMode ? "#9ca3af" : "text.secondary",
            display: "block",
            mb: 1.25,
          }}
        >
          Example: “More concise”, “More formal”, “Avoid filler”, “Keep citations as-is”.
        </Typography>
        <TextField
          fullWidth
          value={preferences}
          onChange={(e) => savePreferences(e.target.value)}
          placeholder="Your preferences..."
          variant="outlined"
          size="small"
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "12px",
              color: darkMode ? "#e5e7eb" : "text.primary",
            },
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: darkMode ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.1)",
            },
          }}
        />
      </Paper>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: "24px",
              bgcolor: darkMode ? "#030303" : "background.paper",
              border: `1px solid ${darkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.06)"}`,
              height: { xs: "auto", md: 550 },
              minHeight: { xs: 300, md: "auto" },
              display: "flex",
              flexDirection: "column",
              transition: "border-color 0.2s",
              "&:focus-within": { borderColor: "rgba(59, 130, 246, 0.5)" },
            }}
          >
            <Box sx={{ p: 3, flexGrow: 1 }}>
              <TextField
                fullWidth
                multiline
                placeholder="Paste AI-generated text here (e.g. from ChatGPT, Claude)..."
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                variant="standard"
                onPaste={handlePaste}
                InputProps={{
                  disableUnderline: true,
                  sx: {
                    fontFamily: "'Inter', sans-serif",
                    fontSize: { xs: "15px", md: "16px" },
                    lineHeight: 1.8,
                    letterSpacing: "0.01em",
                    color: darkMode ? "#e5e7eb" : "#1f2937",
                    height: "100%",
                    alignItems: "flex-start",
                    padding: "8px 0",
                    "& textarea": {
                      height: "100% !important",
                      minHeight: { xs: "180px", md: "auto" },
                    },
                    "& textarea::selection": {
                      backgroundColor: "rgba(99, 102, 241, 0.22)",
                      color: "inherit",
                    },
                  },
                }}
                sx={{ height: "100%" }}
              />
            </Box>
            <Box
              sx={{
                p: 2.5,
                borderTop: `1px solid ${darkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.06)"}`,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                bgcolor: darkMode
                  ? "rgba(255,255,255,0.01)"
                  : "rgba(0,0,0,0.02)",
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <Button
                  size="small"
                  onClick={handleClear}
                  sx={{
                    color: darkMode ? "#4b5563" : "text.secondary",
                    textTransform: "none",
                    fontWeight: 500,
                  }}
                >
                  Clear
                </Button>
                <Typography
                  variant="caption"
                  sx={{
                    color: darkMode ? "#4b5563" : "text.secondary",
                    ml: 1,
                    display: { xs: "none", sm: "block" },
                  }}
                >
                  {inputText.split(/\s+/).filter((x) => x).length} words
                </Typography>
              </Stack>
              <Button
                variant="contained"
                onClick={handleHumanize}
                disabled={loading || !inputText.trim()}
                startIcon={
                  loading ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <AutoFixHigh />
                  )
                }
                sx={{
                  borderRadius: "12px",
                  bgcolor: "#3b82f6",
                  color: "white",
                  textTransform: "none",
                  px: { xs: 2.5, sm: 4 },
                  py: 1,
                  fontSize: { xs: "14px", sm: "15px" },
                  fontWeight: 600,
                  boxShadow: "0 4px 14px 0 rgba(59, 130, 246, 0.4)",
                  "&:hover": {
                    bgcolor: "#2563eb",
                    boxShadow: "0 6px 20px 0 rgba(59, 130, 246, 0.6)",
                  },
                  "&.Mui-disabled": {
                    bgcolor: darkMode
                      ? "rgba(59, 130, 246, 0.2)"
                      : "rgba(0,0,0,0.05)",
                    color: darkMode
                      ? "rgba(255,255,255,0.3)"
                      : "rgba(0,0,0,0.25)",
                    boxShadow: "none",
                  },
                }}
              >
                {loading
                  ? jobStatus === "pending"
                    ? "Queued..."
                    : jobStatus === "processing"
                    ? "Processing..."
                    : "Humanizing..."
                  : isLongRequest
                  ? "Humanize (Async)"
                  : "Humanize Text"}
              </Button>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: "24px",
              bgcolor: darkMode ? "#0a0a0a" : "background.paper",
              border: `1px solid ${darkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.06)"}`,
              height: { xs: "auto", md: 550 },
              minHeight: { xs: 300, md: "auto" },
              display: "flex",
              flexDirection: "column",
              position: "relative",
              overflow: "hidden",
            }}
          >
            {loading && (
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  zIndex: 10,
                }}
              >
                <LinearProgress
                  sx={{
                    height: 3,
                    bgcolor: "transparent",
                    "& .MuiLinearProgress-bar": { bgcolor: "#3b82f6" },
                  }}
                />
              </Box>
            )}
            <Box
              sx={{
                p: 2,
                borderBottom: `1px solid ${darkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.06)"}`,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: darkMode ? "#9ca3af" : "text.secondary",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <VerifiedUser fontSize="small" sx={{ color: "#10b981" }} />{" "}
                Humanized Output
              </Typography>
              <Tooltip
                title={copied ? "Copied!" : "Copy to clipboard"}
                open={copied || undefined}
                placement="top"
              >
                <span>
                  <IconButton
                    onClick={handleCopy}
                    size="small"
                    disabled={!outputText}
                    sx={{
                      color: copied
                        ? "#10b981"
                        : darkMode
                          ? "#6b7280"
                          : "text.secondary",
                      transition: "all 0.2s",
                      "&:hover": { color: darkMode ? "white" : "primary.main" },
                    }}
                  >
                    <ContentCopy fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
            <Box sx={{ p: 3, flexGrow: 1, overflowY: "auto" }}>
              {/* Async job status indicator */}
              {loading && jobStatus && jobStatus !== "done" && (
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", gap: 2 }}>
                  <CircularProgress size={32} sx={{ color: "#3b82f6" }} />
                  <Typography variant="body2" sx={{ color: darkMode ? "#9ca3af" : "text.secondary", fontWeight: 500 }}>
                    {jobStatus === "pending" ? "Queued — waiting to start..." : "AI is working on your text..."}
                  </Typography>
                  <Typography variant="caption" sx={{ color: darkMode ? "#6b7280" : "text.disabled" }}>
                    This may take 20–60 seconds for long texts
                  </Typography>
                </Box>
              )}
              {outputText ? (
                <Box>
                  {/* Judge score chip — Faz 2.4 */}
                  {judgeScore && (
                    <Box sx={{ mb: 1.5 }}>
                      <Chip
                        size="small"
                        label={`Human Score: ${judgeScore.human_score}/100`}
                        sx={{
                          fontWeight: 700,
                          fontSize: "11px",
                          bgcolor:
                            judgeScore.human_score >= 75
                              ? "rgba(16,185,129,0.12)"
                              : judgeScore.human_score >= 60
                              ? "rgba(245,158,11,0.12)"
                              : "rgba(239,68,68,0.12)",
                          color:
                            judgeScore.human_score >= 75
                              ? "#10b981"
                              : judgeScore.human_score >= 60
                              ? "#f59e0b"
                              : "#ef4444",
                          border: "1px solid",
                          borderColor:
                            judgeScore.human_score >= 75
                              ? "rgba(16,185,129,0.3)"
                              : judgeScore.human_score >= 60
                              ? "rgba(245,158,11,0.3)"
                              : "rgba(239,68,68,0.3)",
                        }}
                      />
                      {judgeScore.issues.length > 0 && (
                        <Typography variant="caption" sx={{ ml: 1, color: darkMode ? "#9ca3af" : "text.secondary" }}>
                          {judgeScore.issues.slice(0, 2).join(", ")}
                        </Typography>
                      )}
                    </Box>
                  )}
                  <Typography
                    sx={{
                      fontSize: { xs: "15px", md: "16px" },
                      lineHeight: 1.8,
                      color: darkMode ? "white" : "text.primary",
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {outputText}
                  </Typography>

                  {outputText.startsWith("[Error:") && (
                    <Box sx={{ mt: 2 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={handleRetryHumanize}
                        disabled={loading || !inputText.trim()}
                        sx={{ borderRadius: "12px", textTransform: "none" }}
                      >
                        Retry
                      </Button>
                    </Box>
                  )}

                  {/* Feedback */}
                  <Box
                    sx={{
                      mt: 2.5,
                      pt: 2,
                      borderTop: `1px solid ${darkMode ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)"}`,
                    }}
                  >
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: "12px",
                        bgcolor: darkMode ? "rgba(255,255,255,0.03)" : "#f8fafc",
                        border: "1px dashed",
                        borderColor: darkMode
                          ? "rgba(255,255,255,0.1)"
                          : "#e2e8f0",
                        textAlign: "center",
                      }}
                    >
                      {feedbackStatus === "success" ? (
                        <Box>
                          <Typography
                            variant="body2"
                            sx={{ color: "#10b981", fontWeight: 600, mb: 1 }}
                          >
                            ✓ Thank you! Your feedback was saved.
                          </Typography>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={improveWithFeedback}
                            disabled={improving || loading}
                            sx={{
                              borderRadius: "12px",
                              textTransform: "none",
                              fontWeight: 700,
                            }}
                          >
                            {improving ? "Improving..." : "Generate improved version"}
                          </Button>
                        </Box>
                      ) : pendingLabel ? (
                        <Box>
                          <Typography
                            variant="caption"
                            display="block"
                            sx={{
                              mb: 1,
                              color: darkMode ? "#9ca3af" : "text.secondary",
                              fontWeight: 500,
                            }}
                          >
                            How natural does this read (0–100)?
                          </Typography>
                          <Stack
                            direction="row"
                            spacing={3}
                            alignItems="center"
                            sx={{ px: 2, mb: 2.5 }}
                          >
                            <Slider
                              value={naturalnessScore}
                              onChange={(_, val) => {
                                const v = val as number;
                                setNaturalnessScore(v);
                                if (v <= 10)
                                  setAffectedSections(["Intro", "Body", "End"]);
                              }}
                              min={0}
                              max={100}
                              step={1}
                              sx={{
                                color:
                                  pendingLabel === "AI" ? "#f97316" : "#10b981",
                              }}
                            />
                            <Typography
                              variant="h6"
                              sx={{
                                minWidth: 50,
                                fontWeight: 700,
                                color: darkMode ? "white" : "text.primary",
                              }}
                            >
                              {naturalnessScore}%
                            </Typography>
                          </Stack>

                          <Typography
                            variant="caption"
                            display="block"
                            sx={{
                              mb: 1,
                              color: darkMode ? "#9ca3af" : "text.secondary",
                              fontWeight: 500,
                            }}
                          >
                            Which parts feel {pendingLabel === "AI" ? "AI-like" : "human"}?
                          </Typography>
                          <Box
                            sx={{
                              mb: 2,
                              display: "flex",
                              justifyContent: "center",
                              gap: 1,
                              flexWrap: "wrap",
                            }}
                          >
                            {["Intro", "Body", "End"].map((sec) => (
                              <Chip
                                key={sec}
                                label={sec}
                                variant={
                                  affectedSections.includes(sec)
                                    ? "filled"
                                    : "outlined"
                                }
                                onClick={() => {
                                  if (affectedSections.includes(sec)) {
                                    setAffectedSections((prev) =>
                                      prev.filter((s) => s !== sec)
                                    );
                                  } else {
                                    setAffectedSections((prev) => [...prev, sec]);
                                  }
                                }}
                                sx={{
                                  borderRadius: "8px",
                                  px: 1.5,
                                  fontWeight: 600,
                                  fontSize: "12px",
                                  bgcolor: affectedSections.includes(sec)
                                    ? pendingLabel === "AI"
                                      ? "#fde6d7"
                                      : "#dcfce7"
                                    : "transparent",
                                  color: affectedSections.includes(sec)
                                    ? pendingLabel === "AI"
                                      ? "#ea580c"
                                      : "#16a34a"
                                    : darkMode
                                      ? "#9ca3af"
                                      : "text.secondary",
                                  borderColor: affectedSections.includes(sec)
                                    ? pendingLabel === "AI"
                                      ? "#f97316"
                                      : "#10b981"
                                    : "divider",
                                }}
                              />
                            ))}
                          </Box>

                    <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 1 }}>
                      {[
                        "Too robotic",
                        "Too formal",
                        "Too wordy",
                        "Changed meaning",
                        "Bad flow",
                        "Grammar issues",
                        "AI-like punctuation",
                      ].map((t) => (
                        <Button
                          key={t}
                          size="small"
                          variant={feedbackTags.includes(t) ? "contained" : "outlined"}
                          onClick={() => toggleTag(t)}
                          disabled={feedbackStatus === "loading"}
                          sx={{
                            borderRadius: "999px",
                            textTransform: "none",
                            mb: 1,
                          }}
                        >
                          {t}
                        </Button>
                      ))}
                    </Stack>

                    <TextField
                      fullWidth
                      multiline
                      minRows={2}
                      value={feedbackNotes}
                      onChange={(e) => setFeedbackNotes(e.target.value)}
                      placeholder="Optional notes: what would you change?"
                      size="small"
                      sx={{
                        "& .MuiOutlinedInput-root": { borderRadius: "12px" },
                      }}
                    />

                          <Stack
                            direction="row"
                            spacing={1}
                            justifyContent="center"
                            sx={{ mt: 1.25, position: "relative", zIndex: 1 }}
                          >
                            <Button
                              type="button"
                              variant="text"
                              size="small"
                              onClick={() => setPendingLabel(null)}
                              sx={{
                                color: darkMode ? "#9ca3af" : "text.secondary",
                                fontSize: "11px",
                              }}
                            >
                              Cancel
                            </Button>
                            <Button
                              type="button"
                              variant="contained"
                              size="small"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                submitFeedback();
                              }}
                              disabled={feedbackStatus === "loading"}
                              sx={{
                                bgcolor:
                                  pendingLabel === "AI" ? "#f97316" : "#10b981",
                                fontSize: "11px",
                                boxShadow: "none",
                                pointerEvents: "auto",
                                "&:hover": {
                                  bgcolor:
                                    pendingLabel === "AI" ? "#ea580c" : "#059669",
                                  boxShadow: "none",
                                },
                              }}
                            >
                              {feedbackStatus === "loading"
                                ? "Sending..."
                                : "Submit Report"}
                            </Button>
                          </Stack>
                        </Box>
                      ) : (
                        <>
                          <Typography
                            variant="caption"
                            display="block"
                            sx={{
                              mb: 1.5,
                              color: darkMode ? "#9ca3af" : "text.secondary",
                              fontWeight: 500,
                            }}
                          >
                            Is this result unnatural? Help us improve:
                          </Typography>
                          <Stack
                            direction="row"
                            spacing={1}
                            justifyContent="center"
                          >
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => {
                                setPendingLabel("Human");
                                setNaturalnessScore(90);
                              }}
                              sx={{
                                fontSize: "11px",
                                py: 0.5,
                                borderColor: "#10b981",
                                color: "#10b981",
                                "&:hover": {
                                  borderColor: "#059669",
                                  bgcolor: "rgba(16, 185, 129, 0.05)",
                                },
                              }}
                            >
                              Feels Human
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => {
                                setPendingLabel("AI");
                                setNaturalnessScore(10);
                                setAffectedSections(["Intro", "Body", "End"]);
                              }}
                              sx={{
                                fontSize: "11px",
                                py: 0.5,
                                borderColor: "#f97316",
                                color: "#f97316",
                                "&:hover": {
                                  borderColor: "#ea580c",
                                  bgcolor: "rgba(249, 115, 22, 0.05)",
                                },
                              }}
                            >
                              Feels AI-like
                            </Button>
                          </Stack>
                        </>
                      )}
                    </Box>

                    {/* External Tool Score Reporter */}
                    <Box
                      sx={{
                        mt: 2,
                        pt: 2,
                        borderTop: `1px solid ${darkMode ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.07)"}`,
                      }}
                    >
                      <Typography
                        variant="caption"
                        display="block"
                        sx={{
                          mb: 1,
                          color: darkMode ? "#9ca3af" : "text.secondary",
                          fontWeight: 500,
                        }}
                      >
                        Checked on an external tool? Teach the agent:
                      </Typography>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <Stack direction="row" spacing={0.5}>
                          {["Quillbot", "GPTZero", "Turnitin"].map((t) => (
                            <Chip
                              key={t}
                              label={t}
                              size="small"
                              onClick={() => setExternalTool(t)}
                              variant={externalTool === t ? "filled" : "outlined"}
                              sx={{
                                fontSize: "10px",
                                cursor: "pointer",
                                bgcolor: externalTool === t ? (darkMode ? "#6366f1" : "#4f46e5") : "transparent",
                                color: externalTool === t ? "#fff" : (darkMode ? "#9ca3af" : "text.secondary"),
                                borderColor: darkMode ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.15)",
                              }}
                            />
                          ))}
                        </Stack>
                        <TextField
                          size="small"
                          type="number"
                          placeholder="AI % (e.g. 95)"
                          value={externalScore}
                          onChange={(e) => {
                            const v = Number(e.target.value);
                            if (e.target.value === "" || (v >= 0 && v <= 100)) setExternalScore(e.target.value === "" ? "" : v);
                          }}
                          inputProps={{ min: 0, max: 100 }}
                          sx={{
                            width: 130,
                            "& .MuiInputBase-input": { fontSize: "12px", py: 0.6 },
                          }}
                        />
                        <Button
                          size="small"
                          variant="contained"
                          onClick={reportExternalScore}
                          disabled={externalScore === "" || externalReportStatus === "loading"}
                          sx={{
                            fontSize: "11px",
                            py: 0.5,
                            bgcolor: externalReportStatus === "success" ? "#10b981" : "#6366f1",
                            boxShadow: "none",
                            "&:hover": { bgcolor: "#4f46e5", boxShadow: "none" },
                          }}
                        >
                          {externalReportStatus === "loading" ? "Saving..." : externalReportStatus === "success" ? "Learned!" : "Report to Agent"}
                        </Button>
                      </Stack>
                    </Box>
                  </Box>
                </Box>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                    opacity: 0.2,
                  }}
                >
                  {getModeIcon(mode)}
                  <Typography
                    variant="body2"
                    sx={{ color: darkMode ? "white" : "text.primary", mt: 2 }}
                  >
                    {mode === "aggressive"
                      ? "Deep Anti-AI Processing Ready"
                      : "Ready to Humanize"}
                  </Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
